import { useState, useEffect, useMemo } from "react";
import { useFilterContext } from "./ContextManager";
import { fetchServerData } from "../utils/fetchServerData";
import { customAirportRouteGeneration } from "../utils/customAirportFunctions";
import { getClosestCountryIso } from "../utils/countryFromCoordinates";

// MUI Components
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";

import { countriesListObj } from "../utils/variables";

// MUI Select will be used to pick custom airport groups
// import InputLabel from '@mui/material/InputLabel';
// import MenuItem from '@mui/material/MenuItem';
// import Select from '@mui/material/Select';

import style from "../css/customAirportForm.module.css";

export function CustomAirportForm({ coordinates }) {
  const {
    updateCustomAirportForm,
    editingAirport,
    updateEditingAirport,
    customAirports,
    updateCustomAirports,
    selectedAirports,
    clearSelectedAirports,
    selectedRoutes,
    clearSelectedRoutes,
    geoJsonData,
  } = useFilterContext();

  // DEBUG
  const DEBUG_COUNTRY = true;
  const dbg = (...args) => {
    if (DEBUG_COUNTRY) console.log("[CustomAirportForm]", ...args);
  };

  const [name, setName] = useState(editingAirport ? editingAirport.name : "");
  const [description, setDescription] = useState(
    editingAirport ? editingAirport.description : "",
  );
  const [group, setGroup] = useState("Default"); // Will be used when grouping of custom airports are implemented
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(""); // State for error message
  const [countryError, setCountryError] = useState(false);
  const [mustReselectCountry, setMustReselectCountry] = useState(false);

  // --- Country picker state ---
  // Store ISO code
  const [selectedCountryCode, setSelectedCountryCode] = useState(
    editingAirport?.country?.iso || "", // pre-fill when editing if country exists
  );
  const [countryInput, setCountryInput] = useState("");

  // Build countries list
  const countriesList = useMemo(() => {
    const list = Object.entries(countriesListObj).map(([code, label]) => ({
      code: code.replace(/_/g, "-"),
      label,
    }));
    // Sort alphabetically by label (name)
    list.sort((a, b) => a.code.localeCompare(b.code));
    return list;
  }, []);

  const selectedCountry = useMemo(
    () => countriesList.find((c) => c.code === selectedCountryCode) || null,
    [countriesList, selectedCountryCode],
  );

  // DEBUG
  useEffect(() => {
    if (!editingAirport) {
      dbg("editingAirport = null");
      return;
    }

    dbg("editingAirport changed", {
      _id: editingAirport._id,
      type: editingAirport.type,
      country: editingAirport.country,
      region: editingAirport.region,
    });
  }, [editingAirport]);

  useEffect(() => {
    // DEBUG
    dbg("prefill effect fired", {
      hasEditingAirport: Boolean(editingAirport),
      countriesListSize: countriesList.length,
    });

    if (!editingAirport) {
      dbg("no editingAirport -> clearing selection");
      setMustReselectCountry(false);
      setSelectedCountryCode("");
      setCountryInput("");
      setCountryError(false);
      return;
    }

    // If not legacy, you can prefill from region or country
    const prefillIso =
      editingAirport.region?.iso || editingAirport.country?.iso || "";

    const hasPrefillInList = countriesList.some((c) => c.code === prefillIso);

    const legacy =
      !editingAirport.country?.iso ||
      editingAirport.country.iso === "XX" ||
      editingAirport.country.iso.includes("-") || // old bad case: "US-TX" stored in country.iso
      !prefillIso ||
      !hasPrefillInList; // cannot represent the stored value in the picker => force reselect

    // DEBUG
    dbg("prefill computed", {
      prefillIso,
      hasPrefillInList,
      legacy,
      stored: {
        countryIso: editingAirport.country?.iso,
        regionIso: editingAirport.region?.iso,
      },
    });

    if (legacy) {
      dbg("legacy=true -> forcing reselection");
      setMustReselectCountry(true);
      setSelectedCountryCode(""); // force user to choose again
      setCountryInput("");
    } else {
      setMustReselectCountry(false);
      setSelectedCountryCode(prefillIso);

      // IMPORTANT: since inputValue is controlled, we must also set the visible input text
      const option = countriesList.find((c) => c.code === prefillIso);
      setCountryInput(option?.label || "");
    }

    setCountryError(false);
  }, [editingAirport, countriesList]);

  useEffect(() => {
    if (editingAirport) return;
    if (coordinates?.lat == null || coordinates?.lon == null) return;
    if (!geoJsonData?.features?.length) return;
    if (selectedCountryCode) return;

    const closestIso = getClosestCountryIso(
      coordinates.lat,
      coordinates.lon,
      geoJsonData,
    );

    dbg("auto country lookup", {
      lat: coordinates.lat,
      lon: coordinates.lon,
      closestIso,
    });

    if (!closestIso) return;

    const option = countriesList.find((c) => c.code === closestIso);

    dbg("auto country matched option", {
      closestIso,
      option,
    });

    if (!option) return;

    setSelectedCountryCode(option.code);
    setCountryInput(option.label);
    setCountryError(false);
    setMustReselectCountry(false);
  }, [
    editingAirport,
    coordinates,
    geoJsonData,
    countriesList,
    selectedCountryCode,
  ]);

  // DEBUG
  useEffect(() => {
    dbg("state change: selectedCountryCode", selectedCountryCode);
  }, [selectedCountryCode]);

  useEffect(() => {
    dbg("state change: countryInput", JSON.stringify(countryInput));
  }, [countryInput]);

  useEffect(() => {
    dbg("derived: selectedCountry", selectedCountry);
  }, [selectedCountry]);

  const handleNameChange = (event) => {
    setName(event.target.value);
  };

  const handleDescriptionChange = (event) => {
    setDescription(event.target.value);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Require a country/region selection from the list
    if (mustReselectCountry && !selectedCountryCode) {
      setCountryError(true);
      return;
    }

    const isValidCountry = countriesList.some(
      (c) => c.code === selectedCountryCode,
    );
    if (!isValidCountry) {
      setCountryError(true);
      return;
    }
    setCountryError(false);

    if (isSubmitting) return;
    setIsSubmitting(true);
    setErrorMessage("");

    // Build the payload with common fields
    const payload = {
      name: name.trim(),
      group,
      description: description.trim(),
    };

    // Normalize selection:
    // - If "NO" -> country.iso = "NO", region cleared
    // - If "US-TX" -> country.iso = "US", region.iso = "US-TX"
    const iso = selectedCountryCode; // e.g. "US-TX" or "NO"
    const baseIso = iso.split("-")[0]; // "US" or "NO"

    const selectedFromList = countriesList.find((c) => c.code === iso);

    // For normal countries, selectedFromList.label is the country name.
    // For US/CA regions, selectedFromList.label is like "Texas, USA" / "Alberta, Canada".
    const stripRegionSuffix = (label, base) => {
      if (!label) return "";
      if (base === "US") return label.replace(/,\s*USA\s*$/i, "").trim();
      if (base === "CA") return label.replace(/,\s*Canada\s*$/i, "").trim();
      return label.trim();
    };

    const countryName =
      baseIso === "US"
        ? "United States"
        : baseIso === "CA"
          ? "Canada"
          : selectedFromList?.label || ""; // for non-region countries

    payload.country = {
      iso: baseIso,
      name: countryName,
    };

    if (iso.includes("-")) {
      payload.region = {
        iso, // e.g. "US-TX"
        name: stripRegionSuffix(selectedFromList?.label, baseIso), // "Texas" / "Alberta"
      };
    } else {
      payload.region = null;
    }

    // If this is a new airport creation (not editing), include coordinates.
    if (!editingAirport && coordinates && coordinates.lat && coordinates.lon) {
      payload.lat = coordinates.lat;
      payload.lon = coordinates.lon;
    }

    try {
      let response;

      if (editingAirport) {
        // For editing, add the airport id and call the update endpoint
        payload._id = editingAirport._id;

        response = await fetchServerData("/updatecustomlocation", {
          method: "PUT",
          body: JSON.stringify(payload),
        });

        updateCustomAirports((prev) => {
          const updatedAirports = prev.airports.map((airport) =>
            airport._id === payload._id ? { ...airport, ...payload } : airport,
          );

          return {
            airports: updatedAirports,
            routes: prev.routes,
          };
        });
      } else {
        // New airport creation
        response = await fetchServerData("/storecustomlocation", {
          method: "POST",
          body: JSON.stringify({
            ...payload,
            lat: coordinates.lat,
            lon: coordinates.lon,
          }),
        });

        const processedData = customAirportRouteGeneration([response.data]);
        updateCustomAirports({
          airports: customAirports.airports.concat(processedData.airports),
          routes: customAirports.routes.concat(processedData.routes),
        });

        if (selectedAirports) clearSelectedAirports();
        if (selectedRoutes) clearSelectedRoutes();
      }

      updateCustomAirportForm(false);
      updateEditingAirport(null);
    } catch (error) {
      console.error(
        editingAirport
          ? "Failed to update custom airport:"
          : "Failed to add custom airport:",
        error,
      );
      setErrorMessage(
        editingAirport
          ? "Failed to update custom airport. Please try again."
          : "Failed to add custom airport. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const cancelCustomAirport = () => {
    updateCustomAirportForm(false);
    updateEditingAirport(null);
  };

  return (
    <>
      <div className={style.formWrapper}>
        <form className={style.customForm} onSubmit={handleSubmit}>
          <div className={style.titleErrorWrapper}>
            <p className={style.title}>
              {editingAirport ? "Edit Custom Airport" : "Custom Airport"}
            </p>{" "}
            {errorMessage && (
              <p className={style.errorMessage}>{errorMessage}</p>
            )}{" "}
            {/* Display error message */}
          </div>

          <TextField
            label="Name"
            autoComplete="off"
            size="small"
            value={name}
            onChange={handleNameChange}
            disabled={isSubmitting}
            inputProps={{ maxLength: 35 }}
            fullWidth
            variant="outlined"
            required
          />
          {/* Country picker */}
          <Autocomplete
            options={countriesList}
            value={selectedCountry}
            // onChange={(event, newValue) => {
            //   setSelectedCountryCode(newValue ? newValue.code : "");
            //   setCountryInput(newValue ? newValue.label : "");
            //   setCountryError(false);
            //   setMustReselectCountry(false);
            // }}
            onChange={(event, newValue) => {
              dbg("Autocomplete onChange", {
                newValue,
                code: newValue?.code,
                label: newValue?.label,
              });

              setSelectedCountryCode(newValue ? newValue.code : "");
              setCountryInput(newValue ? newValue.label : "");
              setCountryError(false);
              setMustReselectCountry(false);
            }}
            inputValue={countryInput}
            // onInputChange={(event, newInput) => setCountryInput(newInput)}
            onInputChange={(event, newInput, reason) => {
              dbg("Autocomplete onInputChange", {
                newInput: JSON.stringify(newInput),
                reason,
              });

              if (reason === "reset") return;
              setCountryInput(newInput);
            }}
            getOptionLabel={(option) => option.label}
            disabled={isSubmitting}
            renderOption={(props, option) => (
              <Box
                component="li"
                sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
                {...props}
              >
                <img
                  loading="lazy"
                  width="20"
                  src={`https://flagcdn.com/w20/${option.code
                    .split("-")[0]
                    .toLowerCase()}.webp`}
                  alt=""
                />
                {option.label} ({option.code})
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Country"
                size="small"
                fullWidth
                required
                error={countryError}
                helperText={
                  countryError
                    ? "Country is required"
                    : mustReselectCountry && !selectedCountryCode
                      ? "This airport was saved with an old format. Please reselect country/region."
                      : ""
                }
              />
            )}
          />
          <TextField
            label="Description"
            autoComplete="off"
            multiline
            rows={4}
            value={description}
            onChange={handleDescriptionChange}
            disabled={isSubmitting}
            inputProps={{ maxLength: 350 }}
            fullWidth
            variant="outlined"
          />
          <div className={style.buttonWrapper}>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <CircularProgress size={24} />
              ) : editingAirport ? (
                "Update"
              ) : (
                "Add"
              )}
            </Button>
            <Button onClick={cancelCustomAirport} disabled={isSubmitting}>
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </>
  );
}
